import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Avatar,
  Button,
  Grid,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  styled,
} from '@mui/material';
import { User } from 'firebase/auth';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';

import googleLogo from '../../icons/google.svg';
import {
  trackClickButtonProfile,
  trackClickIconProfile,
} from '../../utils/analytics/analytics';
import { getCategoryFromPathname } from '../../utils/analytics/navigation';
import { trackSentryError } from '../../utils/analytics/sentry';
import { registerAsync, signOutUser } from '../../utils/auth';
import { useFirebaseLoginState } from '../../utils/hooks';
import { IconKeyboardArrowDown, IconLogout, IconPerson } from './icons';

const MenuMaterial = styled(Menu)({
  elevation: 0,
  '& .MuiPaper-root': {
    width: 200,
  },
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontWeight: 700,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },
}));

const LoadingSkeleton: React.FC = () => (
  <Grid container>
    <Typography variant="body1">
      <Skeleton variant="rectangular" width={180} height={40} />
    </Typography>
  </Grid>
);

const GoogleSignInButton: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const triggerSignInWithPopup = async () => {
    try {
      const category = getCategoryFromPathname(location.pathname);
      await registerAsync(category);
    } catch (error) {
      trackSentryError('Error signing in with popup', { error });
      console.error(error);
    }
  };

  return (
    <Button
      variant="outlined"
      disableElevation={false}
      onClick={triggerSignInWithPopup}
      sx={{
        paddingX: '16px',
        height: 44,
        color: '#1F1F1F',
        background: '#ffffff',
        border: 1,
        borderColor: 'primary.main',
        borderRadius: 40,
        boxShadow: 'none',
        textTransform: 'none',
        '&:hover': {
          border: 1,
          borderColor: 'primary.main',
          bgcolor: 'colors.primary.light',
        },
      }}
    >
      <img
        src={googleLogo}
        alt="Google Logo"
        style={{ height: 20, width: 20, marginRight: '12px' }}
      />
      {t('website.googleAuthButton.signInWithGoogle')}
    </Button>
  );
};

const UserProfileAvatar: React.FC<{ user: User | null }> = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const userPhoto = user?.providerData[0]?.photoURL ?? undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Track profile icon click
    trackClickIconProfile();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = async () => {
    try {
      // Track before navigation/reload to avoid losing the event
      await signOutUser('header');
      navigate('/');
      window.location.reload();
    } catch (e) {
      trackSentryError('Error signing out [header]', { error: e });
      console.error('Error signing out [header]:', e);
    }
  };

  const handleProfileClick = () => {
    // Track before navigation to avoid losing event on immediate route change
    trackClickButtonProfile();
    navigate('/app/profile/account');
  };

  return (
    <Grid container gap={1} alignContent="center" alignItems="center">
      <Grid>
        <Button
          onClick={handleClick}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{ pl: 2 }}
        >
          <Avatar alt="User Avatar" src={userPhoto} />
          <IconKeyboardArrowDown />
        </Button>
      </Grid>
      <MenuMaterial
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <StyledMenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <IconPerson />
          </ListItemIcon>
          {t('website.googleAuthButton.profile')}
        </StyledMenuItem>
        <StyledMenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <IconLogout />
          </ListItemIcon>
          {t('website.googleAuthButton.logout')}
        </StyledMenuItem>
      </MenuMaterial>
    </Grid>
  );
};

export function GoogleAuthProfilePicButton() {
  const { isRegistered, user, isAuthStateLoading } = useFirebaseLoginState();

  if (isAuthStateLoading) {
    return <LoadingSkeleton />;
  }

  return isRegistered ? <UserProfileAvatar user={user} /> : <GoogleSignInButton />;
}
